import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import CalendarPicker from '../util/CalendarPicker';
import ClaimantFields from './ClaimantFields';
import { useSnackbar } from 'notistack';
import { t } from 'i18next';
import Switch from '@mui/material/Switch';
import { transformToHierarchy } from '../util/tree';
import TreeViewComponent from '../util/TreeViewComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faXmark } from '@fortawesome/free-solid-svg-icons';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import UploadIcon from '@mui/icons-material/Upload';
import CloseIcon from '@mui/icons-material/Close';
import ImageUpload from '../util/ImageUpload';
import CategoryTaskPicker from '../timesheetsComp/CategoryTaskPicker';

// стиль react-select — тот же селект-бокс, что в Timesheet → Reports
export const fxSelectStyles: any = {
  control: (b: any, s: any) => ({
    ...b, minHeight: 40, background: 'var(--bg-input)',
    border: s.isFocused ? '1px solid rgba(37,99,235,.5)' : '1px solid var(--fx-border-color)',
    borderRadius: 'var(--radius-input, 5px)',
    boxShadow: s.isFocused ? '0 0 0 2px rgba(37,99,235,.08)' : 'none',
    fontSize: 14, cursor: 'text', '&:hover': { borderColor: 'rgba(37,99,235,.3)' },
  }),
  valueContainer: (b: any) => ({ ...b, padding: '2px 10px' }),
  indicatorSeparator: (b: any) => ({ ...b, background: 'var(--fx-border-color)', margin: '8px 0' }),
  dropdownIndicator: (b: any) => ({ ...b, padding: '0 8px', opacity: .5, color: 'var(--text-color-calendar-week)' }),
  clearIndicator: (b: any) => ({ ...b, padding: '0 4px', opacity: .4 }),
  singleValue: (b: any) => ({ ...b, color: 'var(--text-color)', fontSize: 14 }),
  placeholder: (b: any) => ({ ...b, color: 'var(--text-color-calendar-week)', fontSize: 14 }),
  input: (b: any) => ({ ...b, color: 'var(--text-color)', fontSize: 14 }),
  menu: (b: any) => ({ ...b, background: 'var(--bg-form)', border: '1px solid var(--fx-border-color)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,.15)', overflow: 'hidden', zIndex: 9999 }),
  menuPortal: (b: any) => ({ ...b, zIndex: 9999 }),
  option: (b: any, s: any) => ({
    ...b, fontSize: 13, cursor: 'pointer', padding: '9px 12px',
    background: s.isSelected ? 'rgba(37,99,235,.1)' : s.isFocused ? 'var(--btn-user-form)' : 'transparent',
    color: s.isSelected ? '#2563eb' : 'var(--text-color)',
  }),
  noOptionsMessage: (b: any) => ({ ...b, fontSize: 13, color: 'var(--text-color-calendar-week)' }),
};

// опции категорий с полным путём: "Youth Services > Speak Out > Woodside FM"
export const buildCatOptions = (allCategory: any[]) =>
  (allCategory || []).map((c: any) => {
    const parts: string[] = [c.name];
    let cur: any = c;
    while (cur.parentId) {
      const p = allCategory.find((x: any) => x.id === cur.parentId);
      if (!p) break;
      parts.unshift(p.name);
      cur = p;
    }
    return { value: c.id, label: parts.join(' > ') };
  });

interface TemporaryDrawerProps {
  setShowModalAdd: any,
  is24HourFormat: any
  refreshExpenses: any,
  fields: any,
  access?: any,
}

export default function AddExpenses({ setShowModalAdd, is24HourFormat, refreshExpenses, fields, access }: TemporaryDrawerProps) {
  const [employeeId, setEmployeeId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState(new Date());
  const [isCategoryListVisible, setCategoryListVisible] = useState(false);
  const [allCategory, setAllCategory] = useState<any[]>([]);
  const [categoryName, setCategoryName] = useState<any[]>([]);
  const transformToHierarchyCallback = useCallback(transformToHierarchy, []);
  const [category, setCategory] = useState<any[]>([]);
  const [openCheckModal] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number[]>([]);
  const [department] = useState('');
  const [project] = useState('');
  const [name, setName] = useState('');
  const [sortCode, setSortCode] = useState('');
  const [phone, setPhone] = useState('');
  const [account, setAccount] = useState('');
  const [iban, setIban] = useState('');
  const [financeInfo, setFinanceInfo] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const handleClose = () => setShowModalAdd(false);
  const [active, setActive] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [imgUploadOpen, setImgUploadOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleDateChange = (date: Date) => {
    setStartDate(date);
  };

  const handleChangeAcrive = () => {
    setActive(!active);
  }

  const handleCategoryListClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const handleNodeSelect = (nodeId: number) => {
    setSelectedCategoryId([nodeId]);
    // reflect the chosen category name immediately so the selection is visible
    // even without pressing the extra "select" button
    const selected = allCategory?.find((node) => node.id === nodeId);
    if (selected) setCategoryName(selected.name);
  };

  const handleClickCategoryBox = (event: React.MouseEvent) => {
    // just toggle the dropdown — never revert an already-made selection on close
    setCategoryListVisible((visible) => !visible);
    event.stopPropagation();
  };

  const handleClickAddCategoryId = (selectedId: number) => {
    const selectedCategory = allCategory?.find(node => node.id === selectedId);
    if (selectedCategory) {
      setCategoryName(selectedCategory.name)
      setCategoryListVisible(false);
    } else {
      setCategoryName(['All category'])
      console.log('Category not found');
      setCategoryListVisible(false);
    }
  };

  const STAFF_FIELDS = [
    'name',
    'contact_number',
    'account_number',
    'iban',
    'sort_code',
    'finance_additional_info',
  ];

  const isFieldAvailable = (field: any) => {
    if (field.table_name !== 'expenses') return false;
    if (!field.isRequired) return false;
    if (!isFieldShown(field.column_name)) return false;
    return true;
  };

  const isStaffField = (column: string) => STAFF_FIELDS.includes(column);

  // UX-3: check if a field is required per backend config
  const isRequired = (column: string) =>
    fields.some((f: any) => f.table_name === 'expenses' && f.column_name === column && f.isRequired);

  const getValue = (column: string) => {
    const map: Record<string, any> = {
      name,
      contact_number: phone,
      account_number: account,
      iban,
      sort_code: sortCode,
      finance_additional_info: financeInfo,
      project,
      description,
      nominal_department: department,
    };
    return map[column];
  };

  // BUG-5: human-readable field labels for error messages
  const FIELD_LABELS: Record<string, string> = {
    nominal_department: t('Department'),
    project: t('Project'),
    description: t('Description'),
    name: t('Name'),
    contact_number: t('Phone'),
    account_number: t('AccountNumber'),
    iban: 'IBAN',
    sort_code: t('Sort Code'),
    finance_additional_info: t('FinanceAdditionalInfo'),
  };

  const handleExpenses = async () => {
    try {
      if (selectedCategoryId.length === 0) {
        enqueueSnackbar(t('ChooseCategory'), { variant: 'error' });
        return;
      }

      if (!description.trim()) {
        enqueueSnackbar(`${t('FillRequiredFields')}: ${t('Description')}`, { variant: 'error' });
        return;
      }

      if (!amount.trim()) {
        enqueueSnackbar(t('ChooseAmount'), { variant: 'error' });
        return;
      }

      if (/[.,]$/.test(amount)) {
        enqueueSnackbar(t('AmountInvalidEnd'), { variant: 'error' });
        return;
      }

      // normalize decimal comma to dot — otherwise Number('12,50') === NaN and
      // the backend (@IsNumber) rejects the whole request
      const normalizedAmount = Number(amount.replace(',', '.'));
      if (!Number.isFinite(normalizedAmount)) {
        enqueueSnackbar(t('ChooseAmount'), { variant: 'error' });
        return;
      }

      if (!file) {
        enqueueSnackbar(t('UploadFileRequired'), { variant: 'error' });
        return;
      }

      const maxFileSize = 2 * 1024 * 1024;
      if (file.size > maxFileSize) {
        enqueueSnackbar(t('FileSizeLimit'), { variant: 'error' });
        return;
      }

      const requiredFields = fields.filter(isFieldAvailable);
      const missingFields: string[] = [];
      requiredFields.forEach((f: any) => {
        if (active && isStaffField(f.column_name)) return;
        const value = getValue(f.column_name);
        if (!value || value.toString().trim() === '') {
          missingFields.push(f.column_name);
        }
      });

      if (missingFields.length > 0) {
        // BUG-5: show readable labels, not DB column names
        const labels = missingFields.map(f => FIELD_LABELS[f] ?? f).join(', ');
        enqueueSnackbar(`${t('FillRequiredFields')}: ${labels}`, { variant: 'error' });
        return;
      }

      let payload: any = {
        date: startDate,
        nominal_department: department,
        staff: active,
        description,
        project,
        categoryId: Number(selectedCategoryId[0]),
        status: 'pending',
        amount: normalizedAmount,
        userId: employeeId ?? undefined,
      };
      if (!active) {
        payload = {
          ...payload,
          name,
          contact_number: phone,
          account_number: account,
          iban,
          sort_code: sortCode,
          finance_additional_info: financeInfo,
        };
      }

      const response = await axios.post(`expenses/add`, payload);
      const expId = response.data?.id;
      if (!expId) {
        throw new Error('Document ID not returned from server');
      }

      const formData = new FormData();
      formData.append('payment_document', file);
      await axios.post(`expenses/upload/${expId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      handleClose();
      refreshExpenses();
      setFile(null);
      enqueueSnackbar(t('SavedSuccessfully'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error creating', { variant: 'error' });
      console.error('Error creating:', error);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data: categoryData } = await axios.get('category');
        setAllCategory(categoryData)
        const hierarchyData = transformToHierarchyCallback(categoryData);
        setCategory(hierarchyData);
      } catch (e) {
      }
    })();
  }, [transformToHierarchyCallback]);

  const isFieldShown = (column: string) => {
    const field = fields.find(
      (f: any) => f.table_name === 'expenses' && f.column_name === column
    );
    return field?.isShown;
  };

  return (
    <div className='fx-modal' role="presentation">
      {/* Header */}
      <div className='fx-head'>
        <div className='fx-head-text'>
          <div className='fx-head-title'>{t('AddExpenses')}</div>
          <div className='fx-head-sub'>{t('CreateExpenseClaim')}</div>
        </div>
        <div className='fx-close' onClick={handleClose}><CloseIcon fontSize="small" /></div>
      </div>

      {/* Body */}
      <div className='fx-body'>

        {/* От кого + Супервайзер */}
        <ClaimantFields access={access} value={employeeId} onChange={setEmployeeId} />

        {/* Date + Amount */}
        <div className='fx-row2'>
          <div className='fx-field'>
            <CalendarPicker
              mode="single"
              variant="bare"
              className="fx-input"
              value={startDate}
              onChange={(date: any) => handleDateChange(date)}
              displayFormat="DD/MM/YYYY"
            />
          </div>
          <div className='fx-field'>
            <input
              type="text"
              value={amount}
              title={t('Amount')}
              placeholder={`${t('Amount')} *`}
              className="fx-input"
              onChange={(e: any) => {
                let value = e.target.value;
                value = value.replace(/[^0-9.,]/g, '');
                if ((value.match(/[.,]/g) || []).length > 1) { value = value.slice(0, -1); }
                value = value.replace(/([.,]\d{2})\d+/, '$1');
                setAmount(value);
              }}
            />
          </div>
        </div>

        {/* Category */}
        <div className='fx-field'>
          <CategoryTaskPicker
            nodes={category}
            selectedCategoryId={selectedCategoryId}
            categoryName={categoryName}
            onCategorySelect={(id, name) => {
              if (id === -1) { setSelectedCategoryId([]); setCategoryName([]); }
              else { setSelectedCategoryId([id]); setCategoryName(name as any); }
            }}
            selectedTasks={[]}
            setSelectedTasks={() => {}}
            taskOrTasks={false}
            categoryOnly
            block
          />
        </div>

        {/* Receipt photo upload */}
        <div className='fx-field'>
          <div className='fx-dropzone' onClick={() => setImgUploadOpen(true)}>
            <div className='fx-dz-icon'>{file ? <UploadIcon fontSize="small" /> : <ExitToAppIcon fontSize="small" />}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className='fx-dz-main truncate'>{file ? file.name : t('BrowsePayment')}</div>
              <div className='fx-dz-sub'>{file ? t('BrowsePayment') : 'PNG, JPG, PDF, EML · ≤ 2 MB'}</div>
            </div>
            {file && (
              <FontAwesomeIcon
                icon={faXmark}
                className='cursor-pointer hover:text-red-500'
                onClick={(e) => { e.stopPropagation(); setFile(null); }}
              />
            )}
          </div>
          <ImageUpload
            open={imgUploadOpen}
            onClose={() => setImgUploadOpen(false)}
            onComplete={({ file: f, fileName }) => {
              const named = new File([f], fileName, { type: f.type });
              setFile(named);
            }}
            extraAcceptTypes=".eml,.msg"
            extraExtensions={['.eml', '.msg']}
          />
        </div>

        {/* Description */}
        <div className='fx-field'>
          <textarea
            value={description || ''}
            title={t('Description')}
            placeholder={`${t('Description')} *`}
            className="fx-textarea"
            rows={2}
            onChange={(e: any) => setDescription(e.target.value)}
          />
        </div>

        {/* Staff toggle */}
        <div className='fx-switch-card'>
          <Switch
            checked={active}
            onChange={handleChangeAcrive}
            inputProps={{ 'aria-label': 'controlled' }}
          />
          <div>
            <div className='fx-switch-title cursor-pointer' onClick={handleChangeAcrive}>{t('Staff')}</div>
            <div className='fx-switch-hint'>{t('StaffToggleHint')}</div>
          </div>
        </div>

        {!active && (
          <>
            <div className='fx-section'>{t('AddUserInfo')}</div>

            <div className='fx-row2'>
              {isFieldShown('name') && (
                <div className='fx-field'>
                  <input
                    type="text"
                    value={name || ''}
                    title={t('Name')}
                    placeholder={`${t('Name')}${isRequired('name') ? ' *' : ''}`}
                    className="fx-input"
                    onChange={(e: any) => setName(e.target.value)}
                  />
                </div>
              )}
              {isFieldShown('contact_number') && (
                <div className='fx-field'>
                  <input
                    type="text"
                    value={phone || ''}
                    title={t('Phone')}
                    placeholder={`${t('Phone')}${isRequired('contact_number') ? ' *' : ''}`}
                    className="fx-input"
                    onChange={(e: any) => setPhone(e.target.value)}
                  />
                </div>
              )}
            </div>

            <div className='fx-row2'>
              {isFieldShown('account_number') && (
                <div className='fx-field'>
                  <input
                    type="text"
                    value={account || ''}
                    title={t('AccountNumber')}
                    placeholder={`${t('AccountNumber')}${isRequired('account_number') ? ' *' : ''}`}
                    className="fx-input"
                    onChange={(e: any) => setAccount(e.target.value)}
                  />
                </div>
              )}
              {isFieldShown('sort_code') && (
                <div className='fx-field'>
                  <input
                    type="text"
                    value={sortCode || ''}
                    title={t('Sort Code')}
                    placeholder={`${t('Sort Code')}${isRequired('sort_code') ? ' *' : ''}`}
                    className="fx-input"
                    onChange={(e: any) => setSortCode(e.target.value)}
                  />
                </div>
              )}
            </div>

            {isFieldShown('iban') && (
              <div className='fx-field'>
                <input
                  type="text"
                  value={iban || ''}
                  title="IBAN"
                  placeholder={`${t('IBAN')}${isRequired('iban') ? ' *' : ''}`}
                  className="fx-input"
                  onChange={(e: any) => setIban(e.target.value)}
                />
              </div>
            )}

            {isFieldShown('finance_additional_info') && (
              <div className='fx-field'>
                <textarea
                  value={financeInfo || ''}
                  title={t('FinanceAdditionalInfo')}
                  placeholder={`${t('FinanceAdditionalInfo')}${isRequired('finance_additional_info') ? ' *' : ''}`}
                  className="fx-textarea"
                  rows={2}
                  onChange={(e: any) => setFinanceInfo(e.target.value)}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className='fx-foot'>
        <button
          type="button"
          className='fx-save'
          onClick={() => handleExpenses()}>
          {t('Save')}
        </button>
        <button
          type="button"
          className='fx-cancel'
          onClick={handleClose}>
          {t('Cancel')}
        </button>
      </div>
    </div>
  )
}
